import includes from 'lodash/includes';
import { useContext, useEffect } from 'react';
import { NavigateFunction, Outlet, useNavigate, useLocation } from 'react-router-dom';

import { RegistrationStatusEnum, UserDetail, UserTypeEnum } from '../api';
import { SessionContext } from '../app/contexts/SessionContext';

interface Props {
  acceptedStatuses?: RegistrationStatusEnum[];
  onlyUserProvider?: boolean;
  onlyUserHealthcareEntityRep?: boolean;
}

const handleUnacceptableStatus = (
  user: UserDetail,
  acceptedStatuses: RegistrationStatusEnum[],
  navigate: NavigateFunction
) => {
  if (user.registration_status === RegistrationStatusEnum.SETUP) {
    return navigate('/register/email-confirmation');
  }

  if (user.registration_status === RegistrationStatusEnum.PROVIDER_PROFILE) {
    window.location.href = '/app/provider/onboarding';
    return;
  }

  if (
    !includes(acceptedStatuses, RegistrationStatusEnum.MISSING_INFO) &&
    user.registration_status === RegistrationStatusEnum.MISSING_INFO
  ) {
    return navigate('/register/complete');
  }

  if (
    !includes(acceptedStatuses, RegistrationStatusEnum.PROVISIONAL) &&
    user.registration_status === RegistrationStatusEnum.PROVISIONAL &&
    user.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP
  ) {
    return navigate('/register/provisional/healthcare-entity-rep');
  }

  return navigate('/');
};

export const AuthenticatedRoute = (
  { acceptedStatuses = [], onlyUserProvider, onlyUserHealthcareEntityRep }: Props
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, user, sessionExists } = useContext(SessionContext);

  useEffect(() => {
    if ((!isLoading && !user) || sessionExists === false) {
      const nextPath = encodeURIComponent(location.pathname + location.search);
      return navigate(`/login?next=${nextPath}`);
    }

    if (!!user) {
      if (acceptedStatuses.length > 0 && !includes(acceptedStatuses, user.registration_status)) {
        return handleUnacceptableStatus(user, acceptedStatuses, navigate);
      }

      if (onlyUserProvider && user.user_type !== UserTypeEnum.PROVIDER) {
        return navigate('/');
      }

      if (onlyUserHealthcareEntityRep && user.user_type !== UserTypeEnum.HEALTHCARE_ENTITY_REP) {
        return navigate('/');
      }
    }
  }, [
    isLoading,
    user,
    navigate,
    acceptedStatuses,
    onlyUserProvider,
    onlyUserHealthcareEntityRep,
    location,
  ]);

  if (onlyUserProvider && onlyUserHealthcareEntityRep) {
    throw new Error('Cannot specify both onlyUserProvider and onlyUserHealthcareEntityRep');
  }

  return <Outlet />;
};
